import { useState, useMemo } from 'react'
import {
  X,
  Pencil,
  Trash2,
  Check,
  Loader2,
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  Sparkles,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { getLocalName } from '@/lib/ontology/namespaces'
import { ALL_CLASSES } from '@/lib/ontology/schema'
import type { PropertyDefinition } from '@/types/ontology'
import {
  useInstanceDetails,
  useUpdateInstanceLabel,
  useUpdateInstanceProperty,
  useAddInstanceProperty,
  useRemoveInstanceProperty,
  useDeleteInstance,
} from '@/hooks/useInstances'
import { useEntityFacets } from '@/hooks/useFacetGraph'
import { LinkedFacetsList } from '@/components/sources/LinkedFacetCard'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'

// ============================================================================
// PROPERTY GROUPING
// ============================================================================

const LABEL_URI = 'http://www.w3.org/2000/01/rdf-schema#label'
const DESCRIPTION_URI = 'http://www.intellispot.com/ontology#description'

/** System metadata predicates — shown in a collapsed Metadata disclosure,
 *  read-only. Anything not in here and not the label/description is treated
 *  as a user-editable property. */
const SYSTEM_PROPERTY_URIS = new Set<string>([
  'http://www.intellispot.com/ontology#createdAt',
  'http://www.intellispot.com/ontology#updatedAt',
  'http://www.intellispot.com/ontology#source',
  'http://www.intellispot.com/ontology#confidence',
  'http://www.intellispot.com/ontology#status',
  'http://www.intellispot.com/ontology#normalizedLabel',
  'http://www.intellispot.com/ontology#extractedFrom',
])

/** Convert a property URI / camelCase id to a human-readable label. */
function humanizeProperty(uri: string): string {
  const local = getLocalName(uri)
  return local
    // Insert space before capitals (camelCase → camel Case)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // snake_case / kebab-case → words
    .replace(/[_-]/g, ' ')
    // Capitalize first letter
    .replace(/^./, (c) => c.toUpperCase())
}

/** Format a system metadata value for read-only display. */
function formatMetadataValue(uri: string, raw: string): string {
  if (uri.endsWith('createdAt') || uri.endsWith('updatedAt')) {
    const d = new Date(raw)
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    }
  }
  if (uri.endsWith('confidence')) {
    const n = Number(raw)
    if (!Number.isNaN(n)) return `${Math.round(n)}%`
  }
  return raw
}

/** Suggest an HTML input type for a schema property's xsd range. */
function inputTypeForRange(range: string): 'text' | 'number' | 'date' {
  if (range.endsWith('dateTime') || range.endsWith('date')) return 'date'
  if (range.endsWith('integer') || range.endsWith('decimal') || range.endsWith('float') || range.endsWith('double')) return 'number'
  return 'text'
}

/**
 * Resolve the schema-defined data properties for an entity, deduped across
 * all of its types and stripped of system metadata + label + description
 * (those are handled elsewhere). Object-type properties are excluded too —
 * relations live in the Connected To section.
 */
function getSchemaDataProperties(typeUris: string[]): PropertyDefinition[] {
  const seen = new Set<string>()
  const result: PropertyDefinition[] = []
  for (const typeUri of typeUris) {
    const cls = ALL_CLASSES.find((c) => c.uri === typeUri)
    if (!cls) continue
    for (const p of cls.properties) {
      if (p.type !== 'data') continue
      if (p.uri === LABEL_URI || p.uri === DESCRIPTION_URI) continue
      if (SYSTEM_PROPERTY_URIS.has(p.uri)) continue
      // Also hide common system fields by local-name match (some classes
      // include these in their own property arrays).
      const localName = p.localName.toLowerCase()
      if (localName === 'createdat' || localName === 'updatedat' ||
          localName === 'createddate' || localName === 'updateddate' ||
          localName === 'createdby' || localName === 'modifiedby' ||
          localName === 'source' || localName === 'confidence' ||
          localName === 'status' || localName === 'normalizedlabel' ||
          localName === 'extractedfrom') continue
      if (seen.has(p.uri)) continue
      seen.add(p.uri)
      result.push(p)
    }
  }
  return result
}

// ============================================================================
// COMPONENT
// ============================================================================

interface InstanceDetailPanelProps {
  instanceUri: string
  color?: string
  bgColor?: string
  onClose: () => void
  onNavigate?: (uri: string) => void
  onDeleted?: () => void | Promise<void>
  fullWidth?: boolean
}

export function InstanceDetailPanel({
  instanceUri,
  color = '#8a847a',
  bgColor,
  onClose,
  onNavigate,
  onDeleted,
  fullWidth = false,
}: InstanceDetailPanelProps) {
  const { data, isLoading, refetch } = useInstanceDetails(instanceUri)
  const updateLabel = useUpdateInstanceLabel()
  const updateProperty = useUpdateInstanceProperty()
  const addProperty = useAddInstanceProperty()
  const removeProperty = useRemoveInstanceProperty()
  const deleteInstance = useDeleteInstance()
  const askSkerpa = useSkerpaContextStore(s => s.askSkerpa)

  const [editingLabel, setEditingLabel] = useState(false)
  const [labelValue, setLabelValue] = useState('')
  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState('')
  const [editingProp, setEditingProp] = useState<{ uri: string; value: string; index: number } | null>(null)
  const [editPropValue, setEditPropValue] = useState('')
  const [addingProp, setAddingProp] = useState(false)
  const [newPropName, setNewPropName] = useState('')
  const [newPropValue, setNewPropValue] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [facetsExpanded, setFacetsExpanded] = useState(true)
  const [metadataExpanded, setMetadataExpanded] = useState(false)

  const { facets, count: facetCount, isLoading: facetsLoading } = useEntityFacets(instanceUri)

  const instanceLabel = useMemo(() => {
    if (!data) return getLocalName(instanceUri)
    const labelProp = data.properties[LABEL_URI]
    if (labelProp && labelProp.length > 0) return labelProp[0].value
    return getLocalName(instanceUri)
  }, [data, instanceUri])

  // Schema-defined slots for the entity's type(s) — every property the
  // ontology says this kind of entity can have, populated or empty.
  const schemaProperties = useMemo(
    () => getSchemaDataProperties((data?.types ?? []).map((t) => t.uri)),
    [data?.types]
  )

  // Partition properties: description (body), schema-tracked values,
  // ad-hoc literal values not in the schema, and system metadata.
  const { description, schemaValues, adhocProps, systemProps } = useMemo(() => {
    const description = data?.properties[DESCRIPTION_URI]?.[0]?.value ?? ''
    const schemaValues: Record<string, { value: string; label?: string }[]> = {}
    const adhocProps: Array<[string, { value: string; label?: string }[]]> = []
    const systemProps: Array<[string, { value: string; label?: string }[]]> = []
    const schemaUris = new Set(schemaProperties.map((p) => p.uri))
    if (data) {
      for (const [uri, values] of Object.entries(data.properties)) {
        if (uri === LABEL_URI || uri === DESCRIPTION_URI) continue
        if (SYSTEM_PROPERTY_URIS.has(uri)) { systemProps.push([uri, values]); continue }
        // URI-valued literals duplicate the Connected To section — skip them.
        const literalValues = values.filter(
          (v) => !v.value.startsWith('http://') && !v.value.startsWith('https://')
        )
        if (literalValues.length === 0) continue
        if (schemaUris.has(uri)) {
          schemaValues[uri] = literalValues
        } else {
          adhocProps.push([uri, literalValues])
        }
      }
    }
    return { description, schemaValues, adhocProps, systemProps }
  }, [data, schemaProperties])

  const emptySchemaSlotCount = schemaProperties.filter(
    (p) => !schemaValues[p.uri] || schemaValues[p.uri].length === 0
  ).length

  // ── Mutations ──
  const handleSaveLabel = async () => {
    if (labelValue.trim() && labelValue !== instanceLabel) {
      await updateLabel.mutateAsync({ instanceUri, newLabel: labelValue.trim() })
      await refetch()
    }
    setEditingLabel(false)
  }

  const handleSaveDescription = async () => {
    const trimmed = descriptionDraft.trim()
    if (trimmed === description) { setEditingDescription(false); return }
    if (description) {
      await updateProperty.mutateAsync({
        instanceUri, propertyUri: DESCRIPTION_URI,
        oldValue: description, newValue: trimmed,
      })
    } else if (trimmed) {
      await addProperty.mutateAsync({
        instanceUri, propertyUri: DESCRIPTION_URI, value: trimmed,
      })
    }
    await refetch()
    setEditingDescription(false)
  }

  const handleSaveProp = async () => {
    if (editingProp && editPropValue !== editingProp.value) {
      await updateProperty.mutateAsync({
        instanceUri, propertyUri: editingProp.uri,
        oldValue: editingProp.value, newValue: editPropValue,
      })
      await refetch()
    }
    setEditingProp(null)
  }

  const handleRemoveProp = async (propUri: string, value: string) => {
    await removeProperty.mutateAsync({ instanceUri, propertyUri: propUri, value })
    await refetch()
  }

  const handleAddProp = async () => {
    const name = newPropName.trim()
    const value = newPropValue.trim()
    if (!name || !value) { setAddingProp(false); return }
    // Convert "Created at" → "createdAt" (camelCase) for the predicate local name
    const localName = name
      .replace(/[_-\s]+(.)/g, (_, c: string) => c.toUpperCase())
      .replace(/^./, (c) => c.toLowerCase())
    const propUri = `http://www.intellispot.com/ontology#${localName}`
    await addProperty.mutateAsync({ instanceUri, propertyUri: propUri, value })
    await refetch()
    setNewPropName('')
    setNewPropValue('')
    setAddingProp(false)
  }

  const handleDelete = async () => {
    await deleteInstance.mutateAsync(instanceUri)
    await onDeleted?.()
    onClose()
  }

  const handleAskSkerpa = () => {
    const typeLabel = data?.types?.[0]?.label ?? 'item'
    const prompt = `Look at "${instanceLabel}" (${typeLabel}). Suggest a clearer description and any missing properties grounded in the project's documents and entities. Show the suggestions as inline actions I can apply.`
    askSkerpa(prompt)
  }

  const handleSuggestProperty = (prop: PropertyDefinition) => {
    const typeLabel = data?.types?.[0]?.label ?? 'item'
    const desc = prop.description ? ` (${prop.description})` : ''
    const prompt = `For "${instanceLabel}" — a ${typeLabel} — suggest a value for the "${prop.label}" property${desc}. Ground the suggestion in the project's documents and linked entities. Reply with one inline action I can apply.`
    askSkerpa(prompt)
  }

  const handleFillEmpty = () => {
    const typeLabel = data?.types?.[0]?.label ?? 'item'
    const empties = schemaProperties.filter(
      (p) => !schemaValues[p.uri] || schemaValues[p.uri].length === 0
    )
    if (empties.length === 0) return
    const list = empties.map((p) => `- ${p.label}${p.description ? ` (${p.description})` : ''}`).join('\n')
    const prompt = `For "${instanceLabel}" — a ${typeLabel} — propose values for these empty properties, grounded in the project's documents and linked entities. Reply with one inline action per property.\n\n${list}`
    askSkerpa(prompt)
  }

  // ── Render ──
  const cardStyle = fullWidth
    ? { height: '100%', display: 'flex', flexDirection: 'column' as const }
    : { width: '380px', flexShrink: 0, display: 'flex', flexDirection: 'column' as const }

  if (isLoading) {
    return (
      <Card style={{ ...cardStyle, alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 style={{ width: 28, height: 28, animation: 'spin 1s linear infinite', color: 'var(--color-text-muted)' }} />
      </Card>
    )
  }

  if (!data) {
    return (
      <Card style={{ ...cardStyle, alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>Instance not found</p>
      </Card>
    )
  }

  return (
    <Card style={{ ...cardStyle, overflow: 'hidden', borderColor: 'var(--color-border-subtle)' }}>
      {/* ── Header ── */}
      <CardHeader style={{ borderBottom: '1px solid var(--color-border-subtle)', padding: '14px 16px', backgroundColor: bgColor ?? 'var(--color-bg)' }}>
        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingLabel ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  value={labelValue}
                  onChange={(e) => setLabelValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLabel(); if (e.key === 'Escape') setEditingLabel(false) }}
                  onBlur={handleSaveLabel}
                  autoFocus
                  style={{
                    flex: 1, fontSize: 16, fontWeight: 600, color: 'var(--color-text)',
                    padding: '4px 8px', border: `1px solid ${color}`,
                    borderRadius: 6, outline: 'none', minWidth: 0,
                    boxShadow: `0 0 0 3px ${color}22`,
                  }}
                />
              </div>
            ) : (
              <div
                onClick={() => { setLabelValue(instanceLabel); setEditingLabel(true) }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                title="Click to rename"
              >
                <CardTitle style={{ fontSize: 16, color: 'var(--color-text)', wordBreak: 'break-word', flex: 1 }}>
                  {instanceLabel}
                </CardTitle>
                <Pencil style={{ width: 12, height: 12, color: 'var(--color-fg-faint, #a3a39d)', flexShrink: 0 }} />
              </div>
            )}
            {data.types && data.types.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {data.types.map((type) => (
                  <span
                    key={type.uri}
                    style={{
                      fontSize: 10.5, fontFamily: 'var(--font-mono, "Geist Mono")',
                      color, background: `${color}1a`,
                      padding: '1px 7px', borderRadius: 999,
                    }}
                  >
                    {type.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            <Button
              variant="ghost" size="icon" onClick={handleAskSkerpa}
              title="Ask Skerpa to suggest values"
              style={{ width: 28, height: 28, color: 'var(--color-accent, #e84300)' }}
            >
              <Sparkles style={{ width: 14, height: 14 }} />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} style={{ width: 28, height: 28 }}>
              <X style={{ width: 16, height: 16 }} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent style={{ flex: 1, overflow: 'auto', padding: '16px 16px 12px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* ── Body: Description (always-editable paragraph) ── */}
        <div>
          {editingDescription ? (
            <textarea
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              onBlur={handleSaveDescription}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditingDescription(false) }}
              autoFocus
              rows={3}
              placeholder="Add a description…"
              style={{
                width: '100%', resize: 'vertical',
                fontSize: 13, lineHeight: 1.55,
                color: 'var(--color-text)',
                background: 'var(--color-bg-card, #fff)',
                border: `1px solid ${color}`,
                boxShadow: `0 0 0 3px ${color}22`,
                borderRadius: 8, padding: '8px 10px',
                outline: 'none',
              }}
            />
          ) : (
            <div
              onClick={() => { setDescriptionDraft(description); setEditingDescription(true) }}
              style={{
                cursor: 'text',
                fontSize: 13, lineHeight: 1.55,
                color: description ? 'var(--color-text)' : 'var(--color-fg-faint, #a3a39d)',
                fontStyle: description ? 'normal' : 'italic',
                padding: '4px 0',
                borderRadius: 4,
              }}
              title="Click to edit description"
            >
              {description || 'Add a description…'}
            </div>
          )}
        </div>

        {/* ── Properties — schema-driven slots, populated or empty ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
            <h4 style={{
              fontSize: 10.5, fontFamily: 'var(--font-mono, "Geist Mono")', fontWeight: 600,
              color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em',
              margin: 0,
            }}>
              Properties
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {emptySchemaSlotCount > 0 && (
                <button
                  onClick={handleFillEmpty}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11.5, color: 'var(--color-accent, #e84300)', fontWeight: 500,
                    background: 'transparent', border: 0, cursor: 'pointer',
                    padding: '2px 6px', borderRadius: 4,
                  }}
                  title={`Ask Skerpa to suggest values for the ${emptySchemaSlotCount} empty ${emptySchemaSlotCount === 1 ? 'property' : 'properties'}`}
                >
                  <Sparkles style={{ width: 12, height: 12 }} /> Fill empty
                </button>
              )}
              {!addingProp && (
                <button
                  onClick={() => setAddingProp(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11.5, color: 'var(--color-text-muted)',
                    background: 'transparent', border: 0, cursor: 'pointer',
                    padding: '2px 6px', borderRadius: 4,
                  }}
                  title="Add a custom property"
                >
                  <Plus style={{ width: 12, height: 12 }} /> Add
                </button>
              )}
            </div>
          </div>

          {schemaProperties.length === 0 && adhocProps.length === 0 && !addingProp ? (
            <div style={{
              fontSize: 12.5, color: 'var(--color-fg-faint, #a3a39d)', fontStyle: 'italic',
              padding: '6px 0',
            }}>
              {data.types.length === 0
                ? 'No type — add one to see suggested properties.'
                : 'No schema properties defined for this type.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Schema-defined slots, populated or empty */}
              {schemaProperties.map((prop) => (
                <PropertyRow
                  key={prop.uri}
                  propUri={prop.uri}
                  schemaProp={prop}
                  values={schemaValues[prop.uri] ?? []}
                  color={color}
                  editingProp={editingProp}
                  editPropValue={editPropValue}
                  onStartEdit={(uri, val, idx) => { setEditingProp({ uri, value: val, index: idx }); setEditPropValue(val) }}
                  onChangeEdit={setEditPropValue}
                  onSaveEdit={handleSaveProp}
                  onCancelEdit={() => setEditingProp(null)}
                  onAddValue={async (uri, value) => {
                    if (!value.trim()) return
                    await addProperty.mutateAsync({ instanceUri, propertyUri: uri, value: value.trim() })
                    await refetch()
                  }}
                  onRemove={handleRemoveProp}
                  onSuggest={() => handleSuggestProperty(prop)}
                  onNavigate={onNavigate}
                  isPending={updateProperty.isPending || removeProperty.isPending || addProperty.isPending}
                />
              ))}
              {/* Ad-hoc literal properties not declared in the schema */}
              {adhocProps.length > 0 && schemaProperties.length > 0 && (
                <div style={{
                  marginTop: 6, paddingTop: 6,
                  borderTop: '1px dashed var(--color-border-subtle)',
                  fontSize: 10, fontFamily: 'var(--font-mono, "Geist Mono")',
                  color: 'var(--color-fg-faint, #a3a39d)', textTransform: 'uppercase', letterSpacing: '0.1em',
                }}>
                  Other
                </div>
              )}
              {adhocProps.map(([propUri, values]) => (
                <PropertyRow
                  key={propUri}
                  propUri={propUri}
                  values={values}
                  color={color}
                  editingProp={editingProp}
                  editPropValue={editPropValue}
                  onStartEdit={(uri, val, idx) => { setEditingProp({ uri, value: val, index: idx }); setEditPropValue(val) }}
                  onChangeEdit={setEditPropValue}
                  onSaveEdit={handleSaveProp}
                  onCancelEdit={() => setEditingProp(null)}
                  onRemove={handleRemoveProp}
                  onNavigate={onNavigate}
                  isPending={updateProperty.isPending || removeProperty.isPending}
                />
              ))}
            </div>
          )}

          {addingProp && (
            <div style={{
              marginTop: 8, padding: 10,
              background: 'var(--color-bg-card, #fff)',
              border: `1px solid ${color}`,
              boxShadow: `0 0 0 3px ${color}22`,
              borderRadius: 8,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <input
                type="text" value={newPropName}
                onChange={(e) => setNewPropName(e.target.value)}
                placeholder="Property name (e.g. Owner, Status)"
                autoFocus
                style={{
                  fontSize: 12.5, padding: '6px 8px',
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  outline: 'none', background: 'var(--color-warm-50)',
                }}
              />
              <input
                type="text" value={newPropValue}
                onChange={(e) => setNewPropValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddProp(); if (e.key === 'Escape') setAddingProp(false) }}
                placeholder="Value"
                style={{
                  fontSize: 13, padding: '6px 8px',
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  outline: 'none', background: 'var(--color-warm-50)',
                }}
              />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={() => { setAddingProp(false); setNewPropName(''); setNewPropValue('') }}>
                  Cancel
                </Button>
                <Button
                  size="sm" onClick={handleAddProp}
                  disabled={!newPropName.trim() || !newPropValue.trim() || addProperty.isPending}
                >
                  {addProperty.isPending ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : 'Add'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Connected To ── */}
        {data.outgoing.length > 0 && (
          <div>
            <h4 style={{
              fontSize: 10.5, fontFamily: 'var(--font-mono, "Geist Mono")', fontWeight: 600,
              color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em',
              margin: '0 0 8px',
            }}>
              Connected to
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {data.outgoing.map((rel, i) => (
                <button
                  key={i}
                  onClick={() => onNavigate?.(rel.target)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px',
                    background: 'transparent',
                    border: '1px solid var(--color-border-subtle)',
                    borderRadius: 6,
                    cursor: onNavigate ? 'pointer' : 'default',
                    textAlign: 'left',
                    transition: 'background-color 0.12s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{
                    fontSize: 10.5, fontFamily: 'var(--font-mono, "Geist Mono")',
                    color, background: `${color}1a`,
                    padding: '1px 6px', borderRadius: 999,
                    flexShrink: 0,
                  }}>
                    {humanizeProperty(rel.property)}
                  </span>
                  <span style={{ fontSize: 12.5, color: 'var(--color-text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rel.targetLabel}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Referenced By ── */}
        {data.incoming.length > 0 && (
          <div>
            <h4 style={{
              fontSize: 10.5, fontFamily: 'var(--font-mono, "Geist Mono")', fontWeight: 600,
              color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em',
              margin: '0 0 8px',
            }}>
              Referenced by
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {data.incoming.map((rel, i) => (
                <button
                  key={i}
                  onClick={() => onNavigate?.(rel.source)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px',
                    background: 'transparent',
                    border: '1px solid var(--color-border-subtle)',
                    borderRadius: 6,
                    cursor: onNavigate ? 'pointer' : 'default',
                    textAlign: 'left',
                    transition: 'background-color 0.12s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{ fontSize: 12.5, color: 'var(--color-text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rel.sourceLabel}
                  </span>
                  <Badge style={{ fontSize: 9 }} variant="outline">{humanizeProperty(rel.property)}</Badge>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Linked Facets (kept) ── */}
        {facetCount > 0 && (
          <div>
            <button
              onClick={() => setFacetsExpanded(!facetsExpanded)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 6,
                padding: 0, border: 'none', background: 'none', cursor: 'pointer',
                marginBottom: 8,
              }}
            >
              {facetsExpanded ? <ChevronDown style={{ width: 12, height: 12, color: 'var(--color-text-muted)' }} /> : <ChevronRight style={{ width: 12, height: 12, color: 'var(--color-text-muted)' }} />}
              <FileText style={{ width: 12, height: 12, color: 'var(--color-text-muted)' }} />
              <span style={{
                fontSize: 10.5, fontFamily: 'var(--font-mono, "Geist Mono")', fontWeight: 600,
                color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>
                Linked facets ({facetCount})
              </span>
            </button>
            {facetsExpanded && (
              <div style={{ background: 'var(--color-warm-50)', borderRadius: 8, padding: 10 }}>
                {facetsLoading ? (
                  <Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite', margin: '0 auto', display: 'block' }} />
                ) : (
                  <LinkedFacetsList facets={facets} compact emptyMessage="No linked facets" />
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Empty fallback ── */}
        {schemaProperties.length === 0 && adhocProps.length === 0 && data.outgoing.length === 0 && data.incoming.length === 0 && facetCount === 0 && !description && !addingProp && (
          <div style={{
            padding: '12px',
            background: 'var(--color-warm-50)',
            border: '1px dashed var(--color-border-strong)',
            borderRadius: 8,
            textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          }}>
            <p style={{ fontSize: 12.5, color: 'var(--color-text-secondary)', margin: 0 }}>
              No description, properties, or relationships yet.
            </p>
            <button
              onClick={handleAskSkerpa}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12.5, fontWeight: 500,
                background: 'var(--color-accent, #e84300)',
                color: '#fff', border: 0,
                borderRadius: 6, padding: '6px 12px',
                cursor: 'pointer',
              }}
            >
              <Sparkles style={{ width: 12, height: 12 }} /> Ask Skerpa to fill this in
            </button>
          </div>
        )}

        {/* ── Metadata (collapsed by default, read-only) ── */}
        {systemProps.length > 0 && (
          <div>
            <button
              onClick={() => setMetadataExpanded(!metadataExpanded)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 6,
                padding: 0, border: 'none', background: 'none', cursor: 'pointer',
                marginBottom: metadataExpanded ? 8 : 0,
              }}
            >
              {metadataExpanded ? <ChevronDown style={{ width: 12, height: 12, color: 'var(--color-fg-faint, #a3a39d)' }} /> : <ChevronRight style={{ width: 12, height: 12, color: 'var(--color-fg-faint, #a3a39d)' }} />}
              <span style={{
                fontSize: 10.5, fontFamily: 'var(--font-mono, "Geist Mono")', fontWeight: 600,
                color: 'var(--color-fg-faint, #a3a39d)', textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>
                Metadata · {systemProps.length}
              </span>
            </button>
            {metadataExpanded && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 4,
                fontSize: 11.5, color: 'var(--color-text-muted)',
                paddingLeft: 18,
              }}>
                {systemProps.map(([propUri, values]) =>
                  values.map((v, i) => (
                    <div key={`${propUri}-${i}`} style={{ display: 'contents' }}>
                      <span style={{ fontFamily: 'var(--font-mono, "Geist Mono")', fontSize: 10.5 }}>
                        {humanizeProperty(propUri)}
                      </span>
                      {v.value.startsWith('http') && onNavigate ? (
                        <button
                          onClick={() => onNavigate(v.value)}
                          style={{
                            background: 'none', border: 0, padding: 0, textAlign: 'left',
                            color: 'var(--color-text-secondary)', cursor: 'pointer',
                            textDecoration: 'underline', textDecorationColor: 'var(--color-border-strong)',
                            textUnderlineOffset: 2,
                          }}
                        >
                          {v.label || getLocalName(v.value)}
                        </button>
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          {formatMetadataValue(propUri, v.value)}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Delete (kept, dim) ── */}
        <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--color-border-subtle)' }}>
          {showDeleteConfirm ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ fontSize: 12, color: 'var(--color-danger, #f44336)', textAlign: 'center', margin: 0 }}>
                Delete this entity and all its relationships?
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleteInstance.isPending}>
                  {deleteInstance.isPending ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : 'Delete'}
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontSize: 12, color: 'var(--color-fg-faint, #a3a39d)',
                background: 'transparent', border: 0, padding: '6px 8px', borderRadius: 6,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-danger, #f44336)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-fg-faint, #a3a39d)')}
            >
              <Trash2 style={{ width: 12, height: 12 }} /> Delete entity
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// PROPERTY ROW — humanized label + values with hover-revealed remove
// ============================================================================

interface PropertyRowProps {
  propUri: string
  /** Schema definition when known — drives the empty-slot affordance,
   *  per-property AI suggest, and the input type for new values. */
  schemaProp?: PropertyDefinition
  values: { value: string; label?: string }[]
  color: string
  editingProp: { uri: string; value: string; index: number } | null
  editPropValue: string
  onStartEdit: (propUri: string, value: string, index: number) => void
  onChangeEdit: (v: string) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onAddValue?: (propUri: string, value: string) => void
  onRemove: (propUri: string, value: string) => void
  onSuggest?: () => void
  onNavigate?: (uri: string) => void
  isPending: boolean
}

function PropertyRow({
  propUri, schemaProp, values, color,
  editingProp, editPropValue,
  onStartEdit, onChangeEdit, onSaveEdit, onCancelEdit,
  onAddValue, onRemove, onSuggest, onNavigate, isPending,
}: PropertyRowProps) {
  const [adding, setAdding] = useState(false)
  const [addDraft, setAddDraft] = useState('')
  const isEmpty = values.length === 0
  const label = schemaProp?.label ?? humanizeProperty(propUri)
  const description = schemaProp?.description
  const inputType = schemaProp ? inputTypeForRange(schemaProp.range) : 'text'

  const submitAdd = () => {
    if (addDraft.trim() && onAddValue) onAddValue(propUri, addDraft.trim())
    setAddDraft('')
    setAdding(false)
  }

  return (
    <div
      className="group"
      style={{
        display: 'grid', gridTemplateColumns: '120px 1fr',
        columnGap: 10, rowGap: 4, alignItems: 'baseline',
        padding: '4px 0',
        opacity: isEmpty ? 0.85 : 1,
      }}
    >
      <span
        title={description ? `${label} — ${description}` : (schemaProp ? label : propUri)}
        style={{
          fontSize: 11.5,
          color: isEmpty ? 'var(--color-fg-faint, #a3a39d)' : 'var(--color-text-muted)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontWeight: isEmpty ? 400 : 500,
        }}
      >
        {label}
      </span>

      {isEmpty ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          {adding ? (
            <input
              type={inputType}
              value={addDraft}
              onChange={(e) => setAddDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); if (e.key === 'Escape') { setAdding(false); setAddDraft('') } }}
              onBlur={submitAdd}
              placeholder={description ? description.slice(0, 60) : `Add ${label.toLowerCase()}…`}
              autoFocus
              style={{
                flex: 1, fontSize: 13, padding: '4px 8px',
                border: `1px solid ${color}`, borderRadius: 6,
                outline: 'none', boxShadow: `0 0 0 3px ${color}22`,
                background: 'var(--color-bg-card)',
              }}
            />
          ) : (
            <button
              onClick={() => setAdding(true)}
              style={{
                flex: 1, textAlign: 'left',
                fontSize: 12.5, fontStyle: 'italic',
                color: 'var(--color-fg-faint, #a3a39d)',
                background: 'transparent', border: 0, padding: '2px 4px',
                borderRadius: 4, cursor: 'text',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              title="Click to add a value"
            >
              Add value…
            </button>
          )}
          {onSuggest && !adding && (
            <button
              onClick={onSuggest}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100"
              style={{
                width: 18, height: 18, border: 0, background: 'transparent',
                color: 'var(--color-accent, #e84300)', borderRadius: 4,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'opacity 0.12s, background-color 0.12s',
              }}
              title="Ask Skerpa to suggest a value"
              aria-label="Suggest with Skerpa"
            >
              <Sparkles style={{ width: 11, height: 11 }} />
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {values.map((v, i) => {
            const isEditing = editingProp?.uri === propUri && editingProp?.index === i
            const isUri = v.value.startsWith('http://') || v.value.startsWith('https://')
            if (isEditing) {
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type={inputType}
                    value={editPropValue}
                    onChange={(e) => onChangeEdit(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') onSaveEdit(); if (e.key === 'Escape') onCancelEdit() }}
                    onBlur={onSaveEdit}
                    autoFocus
                    style={{
                      flex: 1, fontSize: 13, padding: '4px 8px',
                      border: `1px solid ${color}`, borderRadius: 6,
                      outline: 'none', boxShadow: `0 0 0 3px ${color}22`,
                      background: 'var(--color-bg-card)',
                    }}
                  />
                  <Button variant="ghost" size="icon" onClick={onSaveEdit} disabled={isPending} style={{ width: 22, height: 22 }}>
                    <Check style={{ width: 12, height: 12, color: 'var(--color-success-fg, #306d3f)' }} />
                  </Button>
                </div>
              )
            }
            return (
              <div
                key={i}
                className="row"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '2px 4px',
                  borderRadius: 4,
                  fontSize: 13, color: 'var(--color-text)',
                  minWidth: 0,
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {isUri && onNavigate ? (
                    <button
                      onClick={() => onNavigate(v.value)}
                      style={{ color, background: 'none', border: 0, padding: 0, cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                    >
                      {v.label || getLocalName(v.value)}
                    </button>
                  ) : (
                    v.value
                  )}
                </span>
                {!isUri && (
                  <button
                    onClick={() => onStartEdit(propUri, v.value, i)}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100"
                    style={{
                      width: 18, height: 18, border: 0, background: 'transparent',
                      color: 'var(--color-fg-muted, #767670)', borderRadius: 4,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'opacity 0.12s, background-color 0.12s',
                    }}
                    title="Edit"
                    aria-label="Edit value"
                  >
                    <Pencil style={{ width: 10, height: 10 }} />
                  </button>
                )}
                {onSuggest && (
                  <button
                    onClick={onSuggest}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100"
                    style={{
                      width: 18, height: 18, border: 0, background: 'transparent',
                      color: 'var(--color-accent, #e84300)', borderRadius: 4,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'opacity 0.12s, background-color 0.12s',
                    }}
                    title="Ask Skerpa for a better value"
                    aria-label="Suggest with Skerpa"
                  >
                    <Sparkles style={{ width: 10, height: 10 }} />
                  </button>
                )}
                <button
                  onClick={() => onRemove(propUri, v.value)}
                  disabled={isPending}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100"
                  style={{
                    width: 18, height: 18, border: 0, background: 'transparent',
                    color: 'var(--color-fg-muted, #767670)', borderRadius: 4,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'opacity 0.12s, color 0.12s',
                  }}
                  title="Remove"
                  aria-label="Remove value"
                >
                  <Trash2 style={{ width: 10, height: 10 }} />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
